"use client";

import { useMemo } from "react";

type TFn = (key: any, vars?: Record<string, any>) => string;

type RawGroup = { label: string; markets: any[] };
type DisplayGroup = {
  label: string;
  markets: any[];
  subSections?: Array<{ key: string; label: string; markets: any[] }>;
};

/**
 * Build the localized, UI-shaped tab structure consumed by the sports
 * markets section.
 *
 * Inputs come from the sports-live API as a flat record of
 * `{ tabKey: { label, markets } }`. This hook:
 *
 *  - Translates labels (Polymarket ships them in English; we honor the
 *    locale via `t`).
 *  - Merges the two "1st Set" sub-tabs (winner + total games) into one
 *    parent tab with sub-sections.
 *  - Promotes the "Series Lines" tab so its row order is Match Winner
 *    → per-Game winners (pulled in from each `map_*` tab) → handicaps
 *    → everything else, mirroring the Polymarket layout.
 *  - Strips the Game-N winner rows from each `map_*` tab (they live in
 *    Series Lines now) and reorders the remainder so the per-map view
 *    reads First Blood → Kill Totals → Both-Teams-X → Quadra/Penta →
 *    Odd/Even, with same-type lines kept in ascending line order.
 */
export function useDisplayByType(
  marketsByType: Record<string, RawGroup> | null | undefined,
  t: TFn,
): Record<string, DisplayGroup> | null {
  return useMemo(() => {
    if (!marketsByType) return null;
    const LABEL_OVERRIDES: Record<string, string> = {
      match_totals: t("sportsTabs.totalGames"),
      set_totals: t("sportsTabs.totalSets"),
      series: t("sportsTabs.seriesLines"),
    };
    const FIRST_SET_KEYS = ["first_set_winner", "first_set_totals"];
    const FIRST_SET_SUB_LABELS: Record<string, string> = {
      first_set_winner: t("sportsTabs.firstSetWinner"),
      first_set_totals: t("sportsTabs.firstSetTotalGames"),
    };
    // Per-tab key → i18n label. Backend ships English `group.label`
    // for the rest (moneyline / game_lines / totals / spreads / etc.)
    // — translate them here so /sports/[slug] honours the locale.
    const TAB_I18N_KEY: Record<string, string> = {
      moneyline: "sportsTabs.moneyline",
      // game_lines is intentionally NOT here — the backend toggles its
      // label between "Moneyline" and "Game Lines" depending on whether
      // the event has spreads/totals on top of the moneyline. Resolve
      // off the actual label below so the same tab key can render as
      // either depending on what's underneath.
      totals: "sportsTabs.totals",
      spreads: "sportsTabs.spreads",
      handicap: "sportsTabs.handicap",
      total_corners: "sportsTabs.totalCorners",
      both_teams_score: "sportsTabs.bothTeamsToScore",
      first_half: "sportsTabs.firstHalf",
      second_half: "sportsTabs.secondHalf",
      match_total: "sportsTabs.matchTotal",
    };
    // English label → i18n key. Used as a secondary lookup when the tab
    // key alone is ambiguous (the same key can carry different labels:
    // game_lines = "Moneyline" or "Game Lines").
    const LABEL_I18N_KEY: Record<string, string> = {
      Moneyline: "sportsTabs.moneyline",
      "Game Lines": "sportsTabs.gameLines",
      "1st Half": "sportsTabs.firstHalf",
      "2nd Half": "sportsTabs.secondHalf",
      "Match Total": "sportsTabs.matchTotal",
      "Total Corners": "sportsTabs.totalCorners",
      "Both Teams to Score": "sportsTabs.bothTeamsToScore",
      Spreads: "sportsTabs.spreads",
      Totals: "sportsTabs.totals",
      Handicap: "sportsTabs.handicap",
    };
    // Related events come back as keys like `related_more-markets`,
    // `related_exact-score`, etc. — map the suffix.
    const RELATED_SUFFIX_I18N_KEY: Record<string, string> = {
      // Backend labels this "Game Lines" but the parent tab is also
      // "Game Lines" → user sees two identical tabs. Surface the slug
      // semantics ("More Markets") so the names disambiguate.
      "more-markets": "sportsTabs.moreMarkets",
      "exact-score": "sportsTabs.exactScore",
      "halftime-result": "sportsTabs.halftimeResult",
      "total-corners": "sportsTabs.corners",
      "total-cards": "sportsTabs.cards",
      "both-teams-to-score": "sportsTabs.btts",
      "first-goalscorer": "sportsTabs.firstGoal",
      "anytime-goalscorer": "sportsTabs.goalscorer",
      "team-top-batter": "sportsTabs.teamTopBatter",
      "most-sixes": "sportsTabs.mostSixes",
      "toss-match-double": "sportsTabs.tossMatchDouble",
    };
    const localiseTabLabel = (key: string, fallback: string): string => {
      if (LABEL_OVERRIDES[key]) return LABEL_OVERRIDES[key];
      // 1. Resolve by english label first — handles labels that can
      //    appear under multiple keys (e.g. "Moneyline" via either
      //    `moneyline` or `game_lines` keys depending on event shape).
      const labelKey = LABEL_I18N_KEY[fallback];
      if (labelKey) return t(labelKey as any);
      // 2. Fall back to key-based mapping.
      const i18nKey = TAB_I18N_KEY[key];
      if (i18nKey) return t(i18nKey as any);
      if (key.startsWith("related_")) {
        const suffix = key.slice("related_".length);
        const relKey = RELATED_SUFFIX_I18N_KEY[suffix];
        if (relKey) return t(relKey as any);
      }
      // Pattern keys: map_1 / set_2 / period_3 / quarter_4 / inning_5
      const m = key.match(/^(map|set|period|quarter|inning)_(\d+)$/);
      if (m) return t(`sportsTabs.${m[1]}` as any, { n: m[2] });
      return fallback;
    };
    const result: Record<string, DisplayGroup> = {};
    let firstSetTab: DisplayGroup | null = null;
    // Esports per-map keys (map_1, map_2, …) carry a child_moneyline
    // for that map ("Game N Winner") plus a long tail of per-map stats
    // (Baron, Dragon, etc.). PM rolls the Game N Winners into the
    // Series Lines tab so users can scan moneyline + per-map winners
    // without tab-hopping; we mirror that.
    const mapKeys = Object.keys(marketsByType).filter((k) => /^map_\d+$/.test(k));
    const seriesGameWinners: any[] = [];
    for (const mapKey of mapKeys) {
      const winners = (marketsByType[mapKey]?.markets || []).filter(
        (m: any) => m.sportsMarketType === "child_moneyline",
      );
      seriesGameWinners.push(...winners);
    }
    for (const [key, group] of Object.entries(marketsByType)) {
      if (FIRST_SET_KEYS.includes(key)) {
        if (!firstSetTab) firstSetTab = { label: t("sportsTabs.firstSet"), markets: [], subSections: [] };
        firstSetTab.subSections!.push({ key, label: FIRST_SET_SUB_LABELS[key] || group.label, markets: group.markets });
        firstSetTab.markets.push(...group.markets);
      } else if (key === "series") {
        // Promote "Series Lines": Match Winner → Game N Winners (single
        // compact row with map chip selector) → handicaps (rendered as a
        // group too, with chip selector for the two directions) →
        // anything else (totals, etc.).
        const moneylineRows = (group.markets || []).filter((m: any) => m.sportsMarketType === "moneyline");
        const handicapRows = (group.markets || []).filter((m: any) => m.sportsMarketType === "map_handicap");
        const otherSeriesRows = (group.markets || []).filter(
          (m: any) => m.sportsMarketType !== "moneyline" && m.sportsMarketType !== "map_handicap",
        );
        result.series = {
          label: t("sportsTabs.seriesLines"),
          markets: [...moneylineRows, ...seriesGameWinners, ...handicapRows, ...otherSeriesRows],
        };
      } else if (/^map_\d+$/.test(key)) {
        // Strip child_moneyline (already pulled into Series Lines) from
        // per-map tabs and reorder so PM-style flow lands: First Blood
        // → Kill Totals → Both Teams … → Quadra/Penta Kill →
        // Odd/Even. The MAP_TYPE_ORDER index keeps anything we forgot
        // about at the bottom in stable input order.
        const MAP_TYPE_ORDER = [
          "first_blood_game",
          "kill_over_under_game",
          "lol_both_teams_baron",
          "lol_both_teams_dragon",
          "lol_both_teams_inhibitors",
          "lol_quadra_kill",
          "lol_penta_kill",
          "lol_odd_even_total_kills",
        ];
        const trimmed = (group.markets || [])
          .filter((m: any) => m.sportsMarketType !== "child_moneyline")
          .map((m: any, idx: number) => ({ m, idx }))
          .sort((a, b) => {
            const aIdx = MAP_TYPE_ORDER.indexOf(a.m.sportsMarketType);
            const bIdx = MAP_TYPE_ORDER.indexOf(b.m.sportsMarketType);
            if (aIdx === -1 && bIdx === -1) return a.idx - b.idx;
            if (aIdx === -1) return 1;
            if (bIdx === -1) return -1;
            // Same type: keep ascending line order (16.5 < 17.5 < 18.5)
            if (aIdx === bIdx && typeof a.m.line === "number" && typeof b.m.line === "number") {
              return a.m.line - b.m.line;
            }
            return aIdx - bIdx;
          })
          .map(({ m }) => m);
        if (trimmed.length > 0) {
          result[key] = { label: localiseTabLabel(key, group.label), markets: trimmed };
        }
      } else {
        result[key] = { label: localiseTabLabel(key, group.label), markets: group.markets };
      }
    }
    if (firstSetTab) result.first_set = firstSetTab;
    return result;
  }, [marketsByType, t]);
}
